import { createContext, useContext, useEffect, useState, useCallback } from 'react';
import { api } from '../api.js';
import { resetActivityCache } from '../hooks/useActivity.js';
import { resetNavCounts } from '../hooks/useNavCounts.js';
import { clearToastHistory } from '../components/Toasts.jsx';
import { resetSessionFlags } from '../util/sessionFlags.js';
import { clearAiCache } from '../hooks/useAiCache.js';
import { setViewsUser } from '../util/savedViews.js';

const AuthContext = createContext(null);

// T290 — these hold per-account data at module scope and nothing in this
// app reloads the page when a session ends, so the next person to sign in on
// this tab would paint the previous one's activity rows, sidebar badges and
// toast history until their own fetches landed. Every path that drops `user`
// must call this; there are two (logout, and a 401 from any authed call).
function clearAccountScopedState() {
  resetActivityCache();
  resetNavCounts();
  clearToastHistory();
  resetSessionFlags();
  clearAiCache();
  setViewsUser(null);
}

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);

  const refresh = useCallback(async () => {
    try {
      const me = await api.get('/auth/me');
      setUser(me);
      // Saved views are keyed per account — see util/savedViews.js. Set before
      // any page renders, which is what `loading` below gates.
      setViewsUser(me?.id);
    } catch {
      setUser(null);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    refresh();
  }, [refresh]);

  // T160 — when any authed API call gets a 401 (session expired/revoked), api.js
  // fires this event. Clear the user so RequireAuth redirects to /login on the
  // next render instead of the app silently dead-ending on stale data.
  useEffect(() => {
    // T290b — this path clears `user` too, and it is the COMMON one: sessions
    // expire far more often than anyone clicks Logout. Wiring the resets only
    // into logout() left the whole leak open on the path most likely to be
    // taken, which is the opposite of a fix.
    const onUnauthorized = () => {
      setUser(null);
      clearAccountScopedState();
    };
    window.addEventListener('hunter:unauthorized', onUnauthorized);
    return () => window.removeEventListener('hunter:unauthorized', onUnauthorized);
  }, []);

  const login = useCallback(async (email, password) => {
    const me = await api.post('/auth/login', { email, password });
    setUser(me);
    setViewsUser(me?.id);
    return me;
  }, []);

  const logout = useCallback(async () => {
    // Clear client auth state regardless of whether the server call succeeds —
    // if the network is down or the cookie already expired, the operator still
    // needs the UI to drop to /login rather than silently no-op (frontend audit L1).
    try {
      await api.post('/auth/logout');
    } catch {
      // T290 — swallowed on purpose, and this is not the `finally` below doing
      // it: without a catch, `logout()` REJECTS, and the one caller wires it as
      // `onClick={logout}` with nowhere for that rejection to go. The local
      // sign-out has already succeeded by then; the operator should not get an
      // unhandled-rejection out of a button that did what he asked. Two callers
      // wire it that way: the sidebar and the onboarding page.
    } finally {
      setUser(null);
      clearAccountScopedState();
    }
  }, []);

  return (
    <AuthContext.Provider value={{ user, loading, login, logout, refresh }}>
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error('useAuth must be used inside <AuthProvider>');
  return ctx;
}
